/**
 * entity model class
 *
 * This file is part of Minasa
 * (c) Kaden und Partner AG
 */
import { globalVar, frontendUrl } from '@/helpers/globals';
import BaseModel from './BaseModel';
import BaseCollection from './BaseCollection';
import dayjs from 'dayjs';
import { User } from '@/models/User';
import { instance as api } from '@/helpers/api';

type StatusMapType = {
    [key: string]: { text: string; icon: string; icon_color: string };
};

export const statusMap: StatusMapType = {
    unsigned: { text: 'neu', icon: 'pencil', icon_color: 'primary' },
    requested: { text: 'angefragt', icon: 'hourglass', icon_color: 'primary' },
    rejected: { text: 'zurückgewiesen', icon: 'rejected', icon_color: 'primary' },
    in_union: { text: 'in Kulturplatz freigegeben', icon: 'world', icon_color: 'ok' },
};

export class Address extends BaseModel {
    defaults() {
        return {
            id: null,
            uuid: null,
            // person or organization
            legal_type: null,
            title: null,
            print_title: null,
            description: null,
            first_name: null,
            surname: null,
            sex: null,
            tel: null,
            mobile: null,
            email: null,
            web: null,
            social_media: null,
            street: null,
            street_nr: null,
            address_supplement: null,
            po_box: null,
            zip: null,
            city: null,
            is_visible: null,
            featured_image_id: null,
            featured_img_crop_config: null,
            category_ids: null,
            categories_texts: null,
            status: null,
            from_web: null,
            rejected_reason: null,
            mailing_list_ids: null,
            country_id: null,
            country_name: null,
            creator_id: null,
            created: null,
            modifier_id: null,
            modified: null,
            is_active: null,
            is_partner: null,
            coord_lon: null,
            coord_lat: null,
            eventfrog_rubric_id: null,
            is_unique: null,
            infomail_tgk: null,
            infomail_tgk_date: null,
            import_protection: false,

            //View properties
            address: null, // GENERATED Column, do not store this value.
            // address, location or event_organizer
            type: null,
            location_id: null,
            location_uuid: null,
            organizer_id: null,
            organizer_uuid: null,
            bp_import_id: null,
            bp_import_origin: null,
            is_foreign_import: null,
            mailing_list_names: null,
            creator: null,
            modifier: null,
            status_in_union_mail_sent: null,
            status_rejected_mail_sent: null,
            status_in_union_sender_id: null,
            status_rejected_sender_id: null,
            status_in_union_sender: null,
            status_rejected_sender: null,
            tags: null,
            tag_names: null,
            eventfrog_rubric: null,
            is_address_contact: null,
            is_address_main_contact: null,
            is_linked_to_event_organizer: null,
            nr_of_contacts: null,

            // Fields linked from Location
            location_opening_hours: null,
        };
    }

    get displayTitle() {
        if (this.first_name || this.surname) {
            let ret = `${this.first_name} ${this.surname}`;
            if (this.title) {
                ret += ` (${this.title})`;
            }
            return ret;
        } else {
            return this.title || '- kein Titel -';
        }
    }

    get is_imported() {
        return this.bp_import_id > 0;
    }

    get humanReadableStatus() {
        const status = this.get('status');
        if (status && statusMap[status]) {
            return statusMap[status].text;
        } else {
            return 'neu';
        }
    }

    get status_icon() {
        const status = this.get('status');
        if (status && statusMap[status]) {
            return statusMap[status].icon;
        } else {
            return '';
        }
    }

    get status_icon_color() {
        const status = this.get('status');
        if (status && statusMap[status]) {
            return statusMap[status].icon_color;
        } else {
            return '';
        }
    }

    get thumbImageUrl() {
        if (!this.featured_image_id) {
            return null;
        }
        let type = 'address';
        let id = this.id;
        if (this.type === 'event_organizer') {
            type = 'organizer';
            id = this.organizer_id;
        }
        if (this.type === 'location') {
            type = 'location';
            id = this.location_id;
        }
        const conf = globalVar('featured_images_config')[type].thumb;
        return `${globalVar('imageroot')}/${type}/${id}/${conf.ident}.${conf.format}`;
    }

    get detailPreviewUrl() {
        return frontendUrl(`/kulturplatz/${this.id}`, { preview: 'enable' });
    }
    get searchPreviewUrl() {
        return frontendUrl(`/kulturplatz/?preview=enable&id=${this.id}#id=${this.id}`);
    }

    mutations() {
        return {
            id: (id: number | string | null) => (Number(id) > 0 ? Number(id) : null),
            created: dayjs,
            modified: dayjs,
            is_unique: Boolean,
            infomail_tgk: Boolean,
            infomail_tgk_date: dayjs,
            is_active: Boolean,
            is_partner: Boolean,
            is_visible: Boolean,
            is_address_contact: Boolean,
            is_address_main_contact: Boolean,
            is_linked_to_event_organizer: Boolean,
        };
    }

    entityName() {
        return 'Address';
    }

    async storeCreator(user: User) {
        await api().post('/address/setCreator', {
            id: this.id,
            creator_id: user.id,
        });
    }
}

export class AddressCollection extends BaseCollection {
    options() {
        return {
            model: Address,
        };
    }

    entityName() {
        return 'Address';
    }
}
